import React from "react";
import { useQuery } from "react-query";
import axios from "../utils/axios";
import toast from "react-hot-toast";

const CalendlyDashboard = () => {
  // Fetch scheduled events from Calendly
  const {
    data: scheduledEventsData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["scheduled-events"],
    async () => {
      const response = await axios.get("/api/appointments/scheduled-events");
      return response.data;
    },
    {
      refetchInterval: 60000, // Refetch every minute
      refetchOnWindowFocus: true,
    }
  );

  const scheduledEvents = scheduledEventsData?.scheduledEvents || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleRefresh = () => {
    refetch();
    toast.success("Refreshed meeting data!");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl md:text-2xl text-gray-900 mb-4 uppercase">
              Calendly Dashboard
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-regular text-red-800 mb-2">
                Error Loading Meetings
              </h3>
              <p className="text-red-600 mb-4">
                {error.response?.data?.message || error.message}
              </p>
              <button onClick={handleRefresh} className="btn-primary btn-md">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl md:text-2xl text-gray-900 mb-4 uppercase">
              📅 Calendly Dashboard
            </h2>
            <p className="text-gray-600">
              View all your upcoming meetings from Calendly
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn-primary btn-md flex items-center"
          >
            <svg
              className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  📅
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-regular text-gray-900">
                  Total Meetings
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {scheduledEvents.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  🕒
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-regular text-gray-900">
                  Today's Meetings
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {
                    scheduledEvents.filter((event) => {
                      const eventDate = new Date(
                        event.start_time
                      ).toDateString();
                      const today = new Date().toDateString();
                      return eventDate === today;
                    }).length
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  📊
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-regular text-gray-900">
                  This Week
                </h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {
                    scheduledEvents.filter((event) => {
                      const eventDate = new Date(event.start_time);
                      const now = new Date();
                      const weekFromNow = new Date(
                        now.getTime() + 7 * 24 * 60 * 60 * 1000
                      );
                      return eventDate >= now && eventDate <= weekFromNow;
                    }).length
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Meetings List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-regular text-gray-900">
              Upcoming Meetings
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="loading-dots mx-auto">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <p className="text-gray-500 mt-4">Loading meetings...</p>
            </div>
          ) : scheduledEvents.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 text-6xl mb-4">📅</div>
              <h3 className="text-lg font-regular text-gray-900 mb-2">
                No Upcoming Meetings Found
              </h3>
              <p className="text-gray-500 mb-6">
                This could mean:
                <br />• No meetings have been scheduled yet
                <br />• All meetings are outside the current date range
                <br />• Users haven't completed their Calendly bookings
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/appointments" className="btn-primary btn-md">
                  📋 Book via Form
                </a>
                <a
                  href={process.env.REACT_APP_CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary btn-md"
                >
                  🔗 Open Calendly
                </a>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {scheduledEvents.map((event, index) => (
                <div key={event.id || index} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-regular text-gray-900 mr-3">
                          {event.name}
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {event.status}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          {formatDate(event.start_time)}
                        </div>

                        <div className="flex items-center text-gray-600">
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {formatTime(event.start_time)} -{" "}
                          {formatTime(event.end_time)}
                        </div>

                        {event.location && (
                          <div className="flex items-center text-gray-600">
                            <svg
                              className="w-4 h-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            {event.location.type === "physical"
                              ? event.location.location
                              : "Video call"}
                          </div>
                        )}

                        {event.event_memberships &&
                          event.event_memberships.length > 0 && (
                            <div className="flex items-center text-gray-600">
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                              </svg>
                              {event.event_memberships.length} attendee(s)
                            </div>
                          )}
                      </div>
                    </div>

                    <div className="ml-4 flex flex-col space-y-2">
                      <a
                        href={event.location?.join_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary btn-sm text-center"
                        disabled={!event.location?.join_url}
                      >
                        Join Meeting
                      </a>

                      <div className="text-xs text-gray-500 text-center">
                        Created:{" "}
                        {new Date(event.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-regular text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <a
                href="/appointments"
                className="block w-full btn-secondary btn-md text-center"
              >
                📅 Schedule New Meeting
              </a>

              <a
                href={process.env.REACT_APP_CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full btn-outline btn-md text-center"
              >
                🔗 Open Calendly Dashboard
              </a>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-regular text-gray-900 mb-4">
              Meeting Tips
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Join meetings 5 minutes early
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Test your camera and microphone beforehand
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Have your questions prepared
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                Keep meeting notes handy
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendlyDashboard;
